/**
 * What is still on screen after it has been removed from the plan.
 *
 * ── The problem this exists to solve, and the one it refuses to solve the mock's way ─────────
 * `Builder planu - tydzien - programator` plays `rowOut` on a deleted set and `cardOut` on a deleted
 * exercise by holding the removal behind a 240ms timer: the row is still in its state, so it is
 * still rendered, and the state changes when the animation ends.
 *
 * That shape is not available here. A removal is a store operation carrying an undo entry, so
 * deferring the mutation makes it interleavable with a Cmd+Z pressed inside the window — the undo
 * would restore a plan the delete had not yet reached, and the delete would then land on top of it.
 * The 7 September round drew the conclusion that the exits therefore could not be played. That was
 * wrong: what must not be deferred is the OPERATION, not the pixels.
 *
 * So the operation commits immediately, and this module answers the question the commit leaves
 * behind — **which keys were on screen a moment ago and are not in the plan any more**. The caller
 * renders those from the view they last had, at the position they last held, for exactly as long as
 * the keyframe runs. Nothing is deferred, nothing is un-deleted, and the store is never consulted
 * about a token it no longer has.
 *
 * ── Undo does not fight a ghost ──────────────────────────────────────────────────────────────
 * A key that comes BACK — which is exactly what an undo inside the animation window does — stops
 * being a departure in the same pass that brings it back. The real row renders at its real position
 * and the ghost is dropped in the same commit, so the two are never on screen together and the
 * trainer never sees the row they restored sitting beside a copy of itself on its way out.
 *
 * ── Pure, and tested at these two entry points ───────────────────────────────────────────────
 * No React, no timers, no DOM. `lib/useDepartures` holds the two pieces of state and the one timer;
 * every decision about what is leaving and where it goes is here.
 */

/** One item that has left the plan and has not finished leaving the screen. */
export interface Departure<T> {
  readonly key: string;
  /** Where it stood in the list it was removed from — the position the ghost is drawn at. */
  readonly index: number;
  /** The view it had at the last render before it went. Never re-read from the store. */
  readonly item: T;
}

/** One row of the merged render list: the live item, or the ghost standing in its old place. */
export interface RenderSlot<T> {
  readonly key: string;
  readonly item: T;
  readonly isDeparting: boolean;
}

interface Keyed {
  readonly key: string;
}

/**
 * The departures after a list changed.
 *
 * Everything in `previous` that is absent from `current` joins the list at the index it held in
 * `previous`; everything already held whose key is back in `current` leaves it. A key that is still
 * absent keeps the index and the view it was first held with — re-reading either from a later
 * render would move the ghost while it is animating.
 *
 * ── A change of SUBJECT is not a removal ─────────────────────────────────────────────────────
 * `subjectChanged` says the two lists are lists of different things — the day panel stepping from
 * Monday to Tuesday, not Monday losing five exercises. Without it every card of the day being left
 * behind is read as a delete: measured on 9 September 2026, stepping Push A → Pull A held NINE
 * cards in a five-card list for 270ms and pushed the body's scroll height from 1324px to 2348px
 * while the outgoing day played its exit over the incoming one. That is what the owner was
 * describing as things jumping in and out.
 *
 * Everything already held is dropped with it. A ghost belongs to the list it left, and the trainer
 * has moved on from that list; drawing it over another day would put a card in a day it is not in.
 */
export function reconcileDepartures<T extends Keyed>(
  previous: readonly T[],
  current: readonly T[],
  held: readonly Departure<T>[],
  subjectChanged = false,
): readonly Departure<T>[] {
  if (subjectChanged) return [];

  const live = new Set(current.map((item) => item.key));
  const kept = held.filter((departure) => !live.has(departure.key));
  const alreadyHeld = new Set(kept.map((departure) => departure.key));
  const gone = previous
    .map((item, index) => ({ key: item.key, index, item }))
    .filter((departure) => !live.has(departure.key) && !alreadyHeld.has(departure.key));
  return [...kept, ...gone];
}

/**
 * The list to render: the live items, with each ghost put back where it stood.
 *
 * Insertion is by ascending index, so two rows deleted together both land where they were rather
 * than both landing at the end. An index past the end of the live list appends — a removal of the
 * last item is the ordinary case, not an edge one.
 */
export function mergeDepartures<T extends Keyed>(
  current: readonly T[],
  departures: readonly Departure<T>[],
): readonly RenderSlot<T>[] {
  if (departures.length === 0) {
    return current.map((item) => ({ key: item.key, item, isDeparting: false }));
  }
  const slots: RenderSlot<T>[] = current.map((item) => ({ key: item.key, item, isDeparting: false }));
  for (const departure of [...departures].sort((a, b) => a.index - b.index)) {
    const at = Math.min(departure.index, slots.length);
    slots.splice(at, 0, { key: departure.key, item: departure.item, isDeparting: true });
  }
  return slots;
}
